/**
 * EEOC Guide Page
 * Comprehensive reference material for EEOC compliance
 */
import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import {
  Shield, ArrowLeft, BookOpen, AlertTriangle, CheckCircle,
  XCircle, Scale, Globe, Users, MessageSquare, FileText
} from 'lucide-react';

const GUIDE_SECTIONS = {
  overview: {
    title: 'Overview',
    icon: BookOpen,
    content: [
      {
        title: 'What is EEOC?',
        text: `The Equal Employment Opportunity Commission (EEOC) is the federal agency that enforces laws against workplace discrimination. These laws protect job applicants and employees from discrimination based on protected characteristics.

EEOC compliance in interviews means asking only job-related questions and avoiding any inquiries that could lead to discrimination based on protected categories.`
      },
      {
        title: 'Why Does This Matter?',
        text: `Violations can result in:
• Costly lawsuits and settlements
• Damage to company reputation
• Individual liability for interviewers
• Loss of qualified candidates
• Hostile work environment claims

Even well-intentioned questions can be problematic if they reveal protected information that could influence hiring decisions.`
      }
    ]
  },
  protected: {
    title: 'Protected Categories',
    icon: Shield,
    categories: [
      { name: 'Age', description: 'Age Discrimination in Employment Act (ADEA) protects individuals 40 years or older', examples: ['When did you graduate?', 'How many years until retirement?', 'Can you keep up with younger colleagues?'] },
      { name: 'Race & Color', description: 'Title VII prohibits discrimination based on race, color, or ethnicity', examples: ['Where are you really from?', 'Is that your natural hair?', 'Questions about racial identity'] },
      { name: 'National Origin', description: 'Protects against discrimination based on birthplace, ancestry, culture, or linguistic characteristics', examples: ['Where were you born?', 'What is your native language?', 'Comments about accents'] },
      { name: 'Religion', description: 'Title VII protects against religious discrimination and requires reasonable accommodation', examples: ['What church do you attend?', 'Will you work on religious holidays?', 'Questions about religious dress'] },
      { name: 'Sex/Gender', description: 'Includes protection for pregnancy, sexual orientation, and gender identity', examples: ['Are you married?', 'Do you plan to have children?', 'Questions about childcare'] },
      { name: 'Pregnancy', description: 'Pregnancy Discrimination Act protects pregnant individuals and those with related conditions', examples: ['Are you pregnant?', 'When is your due date?', 'Will you need maternity leave?'] },
      { name: 'Disability', description: 'ADA prohibits discrimination against qualified individuals with disabilities', examples: ['Do you have any disabilities?', 'How many sick days did you take?', 'Questions about medical history'] },
      { name: 'Genetic Information', description: 'GINA prohibits discrimination based on genetic information including family medical history', examples: ['Family medical history questions', 'Questions about genetic testing', 'Does cancer run in your family?'] }
    ]
  },
  dos: {
    title: 'Do\'s & Don\'ts',
    icon: CheckCircle,
    dos: [
      'Focus on job-related qualifications and skills',
      'Ask about ability to perform essential job functions',
      'Discuss availability for work schedule requirements',
      'Ask about legal authorization to work',
      'Inquire about relevant certifications and licenses',
      'Ask behavioral questions about past work situations',
      'Discuss the job duties and requirements clearly',
      'Document all interview questions and answers'
    ],
    donts: [
      'Ask about age, birth date, or graduation year',
      'Inquire about marital status or family plans',
      'Ask about religious practices or beliefs',
      'Question national origin or citizenship',
      'Ask about disabilities or medical conditions',
      'Inquire about pregnancy or childcare',
      'Ask about military discharge status',
      'Make assumptions based on appearance'
    ]
  },
  redirection: {
    title: 'Redirection Techniques',
    icon: MessageSquare,
    techniques: [
      {
        scenario: 'Candidate mentions age or graduation year',
        wrong: 'So you\'ve been in the industry for 30 years? That\'s a long time!',
        right: 'Thank you for sharing. I\'d love to hear about your recent experience with [specific skill/technology].',
        explanation: 'Acknowledge briefly without dwelling on the age-related information, then redirect to job-relevant topics.'
      },
      {
        scenario: 'Candidate mentions pregnancy or children',
        wrong: 'Oh, congratulations! When are you due? Will you need time off?',
        right: 'Thank you for sharing. Let me tell you about our job requirements and schedule expectations.',
        explanation: 'Do not ask follow-up questions about pregnancy or childcare. Stick to discussing general job requirements.'
      },
      {
        scenario: 'Candidate mentions religious practices',
        wrong: 'What religion are you? Will that interfere with work?',
        right: 'We\'re happy to discuss any schedule accommodations after an offer is made. For now, let\'s focus on your qualifications.',
        explanation: 'Defer accommodation discussions to post-offer stage and redirect to qualifications.'
      },
      {
        scenario: 'Candidate mentions disability or health condition',
        wrong: 'What kind of disability do you have? Will you need accommodations?',
        right: 'We can discuss accommodations if an offer is extended. Can you tell me about your experience with [job requirement]?',
        explanation: 'You can discuss ability to perform essential functions, but not specific disabilities or medical details.'
      },
      {
        scenario: 'Candidate mentions national origin or accent',
        wrong: 'Where is that accent from? Are you a citizen?',
        right: 'Are you legally authorized to work in the US? Great, let\'s discuss your relevant experience.',
        explanation: 'You can verify work authorization but not citizenship or national origin.'
      }
    ]
  },
  scenarios: {
    title: 'Common Scenarios',
    icon: FileText,
    scenarios: [
      {
        situation: 'A candidate mentions they graduated in 1985',
        risk: 'Calculating their age or commenting on career length',
        response: 'Focus on recent, relevant experience: "What technologies have you worked with in your last few roles?"'
      },
      {
        situation: 'A candidate mentions they need to leave by 5pm for daycare pickup',
        risk: 'Assuming they\'re unreliable or asking about family',
        response: 'Discuss schedule requirements: "This role requires occasional overtime. Is that something you can accommodate?"'
      },
      {
        situation: 'A candidate is visibly pregnant',
        risk: 'Asking about due date or maternity leave plans',
        response: 'Treat them like any other candidate. Focus only on their ability to perform the job.'
      },
      {
        situation: 'A candidate uses a wheelchair',
        risk: 'Asking about their disability or making assumptions',
        response: 'Ask only if they can perform essential job functions with or without accommodation.'
      },
      {
        situation: 'A candidate has a strong accent',
        risk: 'Asking where they\'re from or about citizenship',
        response: 'You may ask only: "Are you legally authorized to work in the United States?"'
      }
    ]
  },
  uklaw: {
    title: 'UK Equality Act',
    icon: Globe,
    content: `The UK Equality Act 2010 provides similar protections. Key protected characteristics:

• Age
• Disability
• Gender reassignment
• Marriage and civil partnership
• Pregnancy and maternity
• Race
• Religion or belief
• Sex
• Sexual orientation

UK law also prohibits:
• Direct discrimination
• Indirect discrimination
• Harassment
• Victimisation

The same principles apply: focus on job requirements, avoid personal questions about protected characteristics, and treat all candidates equally.`
  }
};

export default function EEOCGuide() {
  const { linkId } = useParams();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900" data-testid="eeoc-guide">
      {/* Header */}
      <header className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center gap-3">
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => navigate(`/eeoc/test/${linkId}`)}
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div className="w-10 h-10 bg-blue-600 rounded-md flex items-center justify-center">
              <BookOpen className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-semibold text-slate-900 dark:text-white">EEOC Compliance Guide</h1>
              <p className="text-xs text-slate-500 dark:text-slate-400">Reference material for interview compliance</p>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-6 py-8">
        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList className="flex flex-wrap gap-1 h-auto p-1">
            <TabsTrigger value="overview" className="text-xs">Overview</TabsTrigger>
            <TabsTrigger value="protected" className="text-xs">Protected Classes</TabsTrigger>
            <TabsTrigger value="dos" className="text-xs">Do's & Don'ts</TabsTrigger>
            <TabsTrigger value="redirection" className="text-xs">Redirection</TabsTrigger>
            <TabsTrigger value="scenarios" className="text-xs">Scenarios</TabsTrigger>
            <TabsTrigger value="uklaw" className="text-xs">UK Law</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="space-y-6">
              {GUIDE_SECTIONS.overview.content.map((section, idx) => (
                <Card key={idx}>
                  <CardHeader>
                    <CardTitle>{section.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-slate-600 dark:text-slate-400 whitespace-pre-line">{section.text}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Protected Categories Tab */}
          <TabsContent value="protected">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="h-5 w-5" />
                  Protected Categories
                </CardTitle>
                <CardDescription>
                  Federal law protects these characteristics from discrimination in hiring
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Accordion type="single" collapsible className="w-full">
                  {GUIDE_SECTIONS.protected.categories.map((cat, idx) => (
                    <AccordionItem key={idx} value={`cat-${idx}`}>
                      <AccordionTrigger className="hover:no-underline">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline">{cat.name}</Badge>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent>
                        <p className="text-slate-600 dark:text-slate-400 mb-3">{cat.description}</p>
                        <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded border border-red-200 dark:border-red-800">
                          <p className="text-xs font-medium text-red-700 dark:text-red-300 mb-2">
                            <XCircle className="h-4 w-4 inline mr-1" />
                            Do NOT Ask:
                          </p>
                          <ul className="text-sm text-red-600 dark:text-red-400 space-y-1">
                            {cat.examples.map((ex, i) => (
                              <li key={i}>• {ex}</li>
                            ))}
                          </ul>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Do's and Don'ts Tab */}
          <TabsContent value="dos">
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="border-emerald-200 dark:border-emerald-800">
                <CardHeader className="pb-3">
                  <CardTitle className="text-emerald-700 dark:text-emerald-300 flex items-center gap-2">
                    <CheckCircle className="h-5 w-5" />
                    Do's
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {GUIDE_SECTIONS.dos.dos.map((item, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-slate-600 dark:text-slate-400">
                        <CheckCircle className="h-4 w-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>

              <Card className="border-red-200 dark:border-red-800">
                <CardHeader className="pb-3">
                  <CardTitle className="text-red-700 dark:text-red-300 flex items-center gap-2">
                    <XCircle className="h-5 w-5" />
                    Don'ts
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {GUIDE_SECTIONS.dos.donts.map((item, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-slate-600 dark:text-slate-400">
                        <XCircle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Redirection Tab */}
          <TabsContent value="redirection">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MessageSquare className="h-5 w-5" />
                  Redirection Techniques
                </CardTitle>
                <CardDescription>
                  How to professionally redirect when candidates volunteer protected information
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {GUIDE_SECTIONS.redirection.techniques.map((tech, idx) => (
                    <div key={idx} className="p-4 border border-slate-200 dark:border-slate-700 rounded-lg">
                      <h4 className="font-medium text-slate-900 dark:text-white mb-3">{tech.scenario}</h4>
                      
                      <div className="grid md:grid-cols-2 gap-4 mb-3">
                        <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded">
                          <p className="text-xs font-medium text-red-700 dark:text-red-300 mb-1">
                            <XCircle className="h-3 w-3 inline mr-1" />
                            Wrong:
                          </p>
                          <p className="text-sm text-red-600 dark:text-red-400">"{tech.wrong}"</p>
                        </div>
                        <div className="p-3 bg-emerald-50 dark:bg-emerald-900/20 rounded">
                          <p className="text-xs font-medium text-emerald-700 dark:text-emerald-300 mb-1">
                            <CheckCircle className="h-3 w-3 inline mr-1" />
                            Right:
                          </p>
                          <p className="text-sm text-emerald-600 dark:text-emerald-400">"{tech.right}"</p>
                        </div>
                      </div>
                      
                      <p className="text-xs text-slate-500 dark:text-slate-400 italic">{tech.explanation}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Scenarios Tab */}
          <TabsContent value="scenarios">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Common Interview Scenarios
                </CardTitle>
                <CardDescription>
                  Real-world situations and how to handle them compliantly
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {GUIDE_SECTIONS.scenarios.scenarios.map((s, idx) => (
                    <div key={idx} className="p-4 border border-slate-200 dark:border-slate-700 rounded-lg">
                      <div className="flex items-start gap-3 mb-3">
                        <div className="w-8 h-8 bg-slate-100 dark:bg-slate-800 rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-sm font-bold text-slate-600 dark:text-slate-300">{idx + 1}</span>
                        </div>
                        <div>
                          <p className="font-medium text-slate-900 dark:text-white">{s.situation}</p>
                        </div>
                      </div>
                      
                      <div className="ml-11 space-y-2">
                        <div className="flex items-start gap-2">
                          <AlertTriangle className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
                          <p className="text-sm text-amber-700 dark:text-amber-300">
                            <strong>Risk:</strong> {s.risk}
                          </p>
                        </div>
                        <div className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                          <p className="text-sm text-emerald-700 dark:text-emerald-300">
                            <strong>Response:</strong> {s.response}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* UK Law Tab */}
          <TabsContent value="uklaw">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Globe className="h-5 w-5" />
                  UK Equality Act 2010
                </CardTitle>
                <CardDescription>
                  UK employment discrimination laws and protections
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-slate-600 dark:text-slate-400 whitespace-pre-line">
                  {GUIDE_SECTIONS.uklaw.content}
                </p>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Back Button */}
        <div className="mt-8 text-center">
          <Button
            onClick={() => navigate(`/eeoc/test/${linkId}`)}
            className="bg-slate-900 hover:bg-slate-800 dark:bg-slate-100 dark:hover:bg-slate-200 dark:text-slate-900"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Assessment
          </Button>
        </div>
      </main>
    </div>
  );
}
